import type { QCRecordByIdResult } from "@/modules/qc/domain/types/record";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface QCRecordPdfPreviewContentProps {
  record: QCRecordByIdResult;
}

export function QCRecordPdfPreviewContent({
  record,
}: QCRecordPdfPreviewContentProps) {
  const getStatusText = (status: string | undefined) => {
    switch (status) {
      case "pass":
        return "ຜ່ານ";
      case "fail":
        return "ບໍ່ຜ່ານ";
      case "pending":
        return "ກຳລັງກວດສອບ";
      default:
        return status || "-";
    }
  };

  return (
    <div
      id="qc-record-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ບັນທຶກຜົນກວດ QC</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`Record-${record?.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ມາດຕະຖານ:{" "}
              <span className="font-normal">{record?.qcStandard?.name}</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີກວດສອບ:</p>
            <p className="font-normal">
              {formatDateLocal(record?.inspectedAt || "")}
            </p>
            <p className="mt-2 font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Record Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          ບັນທຶກຜົນກວດ QC
        </h2>
        <p className="font-semibold text-gray-700 text-xl">
          ສະຖານະ: {getStatusText(record?.overallStatus)}
        </p>
      </div>

      {/* Section 1: ข้อมูลพื้นฐาน */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ມາດຕະຖານ QC
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {record?.qcStandard?.name || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜູ້ກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {record?.inspector?.name || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {formatDateTimeLocal(record?.inspectedAt || "")}
              </td>
            </tr>
            {record?.location && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ສະຖານທີ່
                </td>
                <td className="p-4 text-sm">{record.location}</td>
              </tr>
            )}
            {record?.workItemType && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ປະເພດງານ
                </td>
                <td className="p-4 text-sm">{record.workItemType.name}</td>
              </tr>
            )}
            {record?.workItemId && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ລະຫັດງານ
                </td>
                <td className="p-4 text-sm">{record.workItemId}</td>
              </tr>
            )}
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສະຖານະ
              </td>
              <td className="p-4 font-semibold text-sm">
                {getStatusText(record?.overallStatus)}
              </td>
            </tr>
          </tbody>
        </table>

        {record?.notes && (
          <div className="mb-6 rounded-lg border-2 border-gray-300 bg-gray-50 p-4">
            <h4 className="mb-2 font-semibold text-gray-700">ຫມາຍເຫດ:</h4>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {record.notes}
            </p>
          </div>
        )}
      </div>

      {/* Section 2: รายการตรวจ */}
      {record?.items && record.items.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ລາຍການກວດສອບ
          </h3>

          <div className="space-y-6">
            {record.items.map((item, index) => {
              const imageUrl = item.photoUrl
                ? resolveImageSrc(item.photoUrl)
                : null;

              return (
                <div
                  key={item.id}
                  className="mb-6 break-inside-avoid rounded-lg border-2 border-gray-300 bg-white p-6"
                >
                  <div className="mb-4 flex items-start justify-between border-gray-200 border-b pb-3">
                    <h4 className="font-bold text-gray-800 text-lg">
                      {index + 1}. {item.standardItem.title}
                    </h4>
                    <span className="font-semibold text-gray-700">
                      {getStatusText(item.status)}
                    </span>
                  </div>

                  {item.standardItem.description && (
                    <p className="mb-4 text-gray-700 text-sm leading-relaxed">
                      {item.standardItem.description}
                    </p>
                  )}

                  {item.notes && (
                    <div className="mb-4 rounded bg-gray-50 p-3">
                      <p className="font-semibold text-gray-700 text-sm">
                        ຫມາຍເຫດ:
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                        {item.notes}
                      </p>
                    </div>
                  )}

                  {imageUrl && (
                    <div className="mb-4 break-inside-avoid">
                      <p className="mb-2 font-semibold text-gray-700 text-sm">
                        ຮູບພາບ:
                      </p>
                      <img
                        src={imageUrl}
                        alt={item.standardItem.title}
                        className="max-w-full rounded border-2 border-gray-300"
                        style={{ maxHeight: "400px", objectFit: "contain" }}
                      />
                    </div>
                  )}

                  {item.checkedAt && (
                    <p className="text-gray-500 text-xs">
                      ກວດສອບເມື່ອ: {formatDateTimeLocal(item.checkedAt)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-12 border-gray-300 border-t-2 pt-6 text-center text-gray-600 text-sm">
        <p>ເອກະສານນີ້ຖືກສ້າງອັດຕະໂນມັດໂດຍລະບົບ Freedom ERP</p>
        <p className="mt-1">
          ວັນທີພິມ: {formatDateTimeLocal(new Date())} | ເລກທີບັນທຶກ: {record?.id}
        </p>
      </div>
    </div>
  );
}
